import { useEffect, useRef, useState } from "react";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import { Input } from "src/components/ui/input";
import { Textarea } from "src/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { Card, CardContent } from "src/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Badge } from "src/components/ui/badge";
import { Separator } from "src/components/ui/separator";
import {
  PlusCircle,
  Trash2,
  Upload,
  FileText,
  Play,
  X,
  Calendar,
  DollarSign,
  Clock,
  GraduationCap,
  Building,
  Languages,
  Briefcase,
  User,
  Mail,
  Phone,
  MapPin,
  Pencil,
  Users2,
  AlertCircle,
  Loader2,
  Lock,
  Eye,
  EyeOff,
  Box,
} from "lucide-react";
import axios from "axios";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../../../../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../../../../context/AuthContext";
import UploadImageProfile from "../../../../../components/common/UploadImageProfile";
import { useIsMobile } from "../../../../../hooks/use-mobile";
import MainButton from "./../../../../../components/MainButton";
import { Select as AntdSelect } from "antd";
import { useAPI } from "../../../../../context/APIContext";

async function createTeacherQualification(data, setLoading) {
  try {
    const response = await axios.post(
      `${BaseUrlApi}/teacher-qualifications`,
      data
    );
    toast("Success", { description: "Qualification created successfully" });
    return response.data;
  } catch (error) {
    toast("Error", { description: ErrorMessage(error) });
    throw error;
  } finally {
    if (setLoading) setLoading(false);
  }
}

async function updateTeacherQualification(qualificationId, data, setLoading) {
  try {
    const response = await axios.put(
      `${BaseUrlApi}/teacher-qualifications/${qualificationId}`,
      data
    );
    toast("Success", { description: "Qualification updated successfully" });
    return response.data;
  } catch (error) {
    toast("Error", { description: ErrorMessage(error) });
    throw error;
  } finally {
    if (setLoading) setLoading(false);
  }
}

async function deleteTeacherQualification(qualificationId, setLoading) {
  try {
    await axios.delete(
      `${BaseUrlApi}/teacher-qualifications/${qualificationId}`
    );
    toast("Success", { description: "Qualification deleted successfully" });
  } catch (error) {
    toast("Error", { description: ErrorMessage(error) });
    throw error;
  } finally {
    if (setLoading) setLoading(false);
  }
}

async function createTeacherExperience(data, setLoading) {
  try {
    const response = await axios.post(
      `${BaseUrlApi}/teacher-experiences`,
      data
    );
    toast("Success", { description: "Experience created successfully" });
    return response.data;
  } catch (error) {
    toast("Error", { description: ErrorMessage(error) });
    throw error;
  } finally {
    if (setLoading) setLoading(false);
  }
}

async function updateTeacherExperience(experienceId, data, setLoading) {
  try {
    const response = await axios.put(
      `${BaseUrlApi}/teacher-experiences/${experienceId}`,
      data
    );
    toast("Success", { description: "Experience updated successfully" });
    return response.data;
  } catch (error) {
    toast("Error", { description: ErrorMessage(error) });
    throw error;
  } finally {
    if (setLoading) setLoading(false);
  }
}

async function deleteTeacherExperience(experienceId, setLoading) {
  try {
    await axios.delete(`${BaseUrlApi}/teacher-experiences/${experienceId}`);
    toast("Success", { description: "Experience deleted successfully" });
  } catch (error) {
    toast("Error", { description: ErrorMessage(error) });
    throw error;
  } finally {
    if (setLoading) setLoading(false);
  }
}

async function updateTeacherProfile(userId, data, setLoading) {
  try {
    const response = await axios.post(
      `${BaseUrlApi}/teacher-profiles/${userId}/update`,
      data
    );
    toast("Success", { description: "Profile updated successfully" });
    return response.data;
  } catch (error) {
    toast("Error", { description: ErrorMessage(error) });
    throw error;
  } finally {
    if (setLoading) setLoading(false);
  }
}

export default function TeacherProfileDialog({
  teacherData,
  getAllTeacherData,
}) {
  const { user } = useAuth();
  const { categoriesItems, coursesItems } = useAPI();
  const { categories, loadingCategories } = categoriesItems;
  const { loadingCourses, courses } = coursesItems;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [loadingQualifications, setLoadingQualifications] = useState(false);
  const [loadingExperiences, setLoadingExperiences] = useState(false);
  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [securityForm, setSecurityForm] = useState({
    newEmail: "",
    currentPasswordForEmail: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loadingPasswordCheck, setLoadingPasswordCheck] = useState(false);

  const handleSecurityInputChange = (e) => {
    const { id, value } = e.target;
    setSecurityForm((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const verifyPassword = async (password) => {
    try {
      setLoadingPasswordCheck(true);
      await axios.post(`${BaseUrlApi}/check-password`, { password });
      return true;
    } catch (error) {
      toast("Error", { description: error.response?.data?.data });
      return false;
    } finally {
      setLoadingPasswordCheck(false);
    }
  };

  // Form state
  const [experiences, setExperiences] = useState(
    teacherData["teacher_experiences"] || []
  );
  const [qualifications, setQualifications] = useState(
    teacherData["teacher_qualifications"] || []
  );
  const [newCourse, setNewCourse] = useState("");
  const [subjects, setSubjects] = useState(teacherData.subjects || []);
  const [newSubject, setNewSubject] = useState("");
  const [languages, setLanguages] = useState(teacherData.languages || []);
  const [newLanguage, setNewLanguage] = useState("");
  const [programs, setPrograms] = useState(teacherData.programs || []);
  const [newProgram, setNewProgram] = useState("");
  const [files, setFiles] = useState(teacherData.files || []);
  const [selectCategories, setSelectCategories] = useState(
    teacherData.categories_id || null
  );
  const [selectCourses, setSelectCourses] = useState(
    teacherData.courses_id || null
  );

  // Form data
  const [formData, setFormData] = useState({
    user: {
      name: teacherData.user.name || null,
      user_name: teacherData.user.user_name || null,
      email: teacherData.user.email || null,
      phone: teacherData.user.phone || null,
      image: teacherData.user.image || null,
      provider: teacherData.user.provider || null,
      provider_id: teacherData.user.provider_id || null,
      lang: teacherData.user.lang || "",
    },
    address: teacherData.address || "",
    age: teacherData.age || "",
    language: teacherData.user.lang || "",
    hour_works: teacherData.hour_works || "",
    experience: teacherData.experience || "",
    rate: teacherData.rate || "",
    status: teacherData.status || "0",
    name_job: teacherData.name_job || "",
    description: teacherData.description || "",
    year_experience: teacherData.year_experience || "",
    programs,
    languages,
    subjects,
    files,
    cv: teacherData.cv || "",
    demo_video: teacherData.demo_video || "",
    teacher_experiences: experiences,
    teacher_qualifications: qualifications,
    categories_id: selectCategories,
    courses_id: selectCourses,
  });

  const cvInputRef = useRef(null);
  const demoVideoInputRef = useRef(null);
  const additionalFilesInputRef = useRef(null);

  const onFileChange = (e, type) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file, type);
    }
  };

  const handleFileUpload = async (file, type) => {
    try {
      const uploadData = new FormData();
      uploadData.append("file", file);
      uploadData.append("name", "teacher_files");

      const response = await axios.post(`${BaseUrlApi}/upload`, uploadData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          toast.loading(`Uploading ${type}... ${percentCompleted}%`, {
            id: `upload-${type}`,
            duration: Infinity,
          });
        },
      });

      const uploadedFilePath = response.data.url;
      setFormData((prev) => {
        if (type === "cv") {
          return { ...prev, cv: uploadedFilePath };
        } else if (type === "demo_video") {
          return { ...prev, demo_video: uploadedFilePath };
        } else if (type === "additional") {
          return { ...prev, files: [...(prev.files || []), uploadedFilePath] };
        }
        return prev;
      });

      toast.dismiss(`upload-${type}`);
      toast.success(`${type.toUpperCase()} uploaded successfully`);
    } catch (error) {
      toast.dismiss(`upload-${type}`);
      toast.error(ErrorMessage(error));
    }
  };

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      programs,
      languages,
      subjects,
      files,
    }));
  }, [programs, languages, subjects, files]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    if (id.startsWith("user.")) {
      const userField = id.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          [userField]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [id]: value,
      }));
    }
  };

  const addExperience = () => {
    setExperiences([
      ...experiences,
      {
        id: Date.now(),
        type: "",
        institution: "",
        year: "",
        ages: "",
        teacher_profile_id: teacherData.user.id,
      },
    ]);
  };

  const updateExperience = (index, field, value) => {
    const updatedExperiences = [...experiences];
    updatedExperiences[index] = {
      ...updatedExperiences[index],
      [field]: value,
    };
    setExperiences(updatedExperiences);
  };

  const removeExperience = async (index) => {
    const experience = experiences[index];
    if (experience.id && experience.id.toString().length < 10) {
      try {
        // setLoadingExperiences(true);
        await deleteTeacherExperience(experience.id, setLoadingExperiences);
        await getAllTeacherData();
      } catch (error) {
        console.error("Error deleting experience:", error);
      }
    }
    setExperiences(experiences.filter((_, i) => i !== index));
  };

  const addQualification = () => {
    setQualifications([
      ...qualifications,
      {
        id: Date.now(),
        qualification: "",
        university: "",
        graduation_year: "",
        teacher_profile_id: teacherData.id,
        courses: [],
      },
    ]);
  };

  const updateQualification = (index, field, value) => {
    const updatedQualifications = [...qualifications];
    updatedQualifications[index] = {
      ...updatedQualifications[index],
      [field]: value,
    };
    setQualifications(updatedQualifications);
  };

  const removeQualification = async (index) => {
    const qualification = qualifications[index];
    if (qualification.id && qualification.id.toString().length < 10) {
      try {
        // setLoadingQualifications(true);
        await deleteTeacherQualification(
          qualification.id,
          setLoadingQualifications
        );
        await getAllTeacherData();
      } catch (error) {
        console.error("Error deleting qualification:", error);
      }
    }
    setQualifications(qualifications.filter((_, i) => i !== index));
  };

  const addCourse = (index) => {
    if (!newCourse.trim()) return;
    const updatedQualifications = [...qualifications];
    updatedQualifications[index].courses = [
      ...(updatedQualifications[index].courses || []),
      newCourse,
    ];
    setQualifications(updatedQualifications);
    setNewCourse("");
  };

  const removeCourse = (qualIndex, courseIndex) => {
    const updatedQualifications = [...qualifications];
    updatedQualifications[qualIndex].courses = updatedQualifications[
      qualIndex
    ].courses.filter((_, i) => i !== courseIndex);
    setQualifications(updatedQualifications);
  };

  const addSubject = () => {
    if (!newSubject.trim()) return;
    setSubjects([...subjects, newSubject]);
    setNewSubject("");
  };

  const removeSubject = (index) => {
    setSubjects(subjects.filter((_, i) => i !== index));
  };

  const addLanguage = () => {
    if (!newLanguage.trim()) return;
    setLanguages([...languages, newLanguage]);
    setNewLanguage("");
  };

  const removeLanguage = (index) => {
    setLanguages(languages.filter((_, i) => i !== index));
  };

  const addProgram = () => {
    if (!newProgram.trim()) return;
    setPrograms([...programs, newProgram]);
    setNewProgram("");
  };

  const removeProgram = (index) => {
    setPrograms(programs.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate personal info
    if (!formData.user.name?.trim())
      newErrors["user.name"] = "Name is required";
    if (!formData.user.email?.trim())
      newErrors["user.email"] = "Email is required";
    if (
      formData.user.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.user.email)
    ) {
      newErrors["user.email"] = "Invalid email format";
    }
    if (
      formData.user.phone &&
      !/^\+?[1-9]\d{1,14}$/.test(formData.user.phone)
    ) {
      newErrors["user.phone"] = "Invalid phone number format";
    }

    // Validate professional info
    if (!formData.name_job?.trim())
      newErrors.name_job = "Job title is required";
    if (!formData.rate) newErrors.rate = "Rate is required";
    if (formData.rate && (isNaN(formData.rate) || Number(formData.rate) <= 0)) {
      newErrors.rate = "Rate must be a positive number";
    }
    if (
      formData.year_experience &&
      (isNaN(formData.year_experience) || Number(formData.year_experience) < 0)
    ) {
      newErrors.year_experience =
        "Years of experience must be a non-negative number";
    }

    // Validate qualifications
    qualifications.forEach((qual, index) => {
      if (!qual.qualification?.trim()) {
        newErrors[`qualification.${index}.qualification`] =
          "Qualification name is required";
      }
      if (!qual.university?.trim()) {
        newErrors[`qualification.${index}.university`] =
          "University name is required";
      }
    });

    // Validate experiences
    experiences.forEach((exp, index) => {
      if (!exp.type?.trim()) {
        newErrors[`experience.${index}.type`] = "Experience type is required";
      }
      if (!exp.institution?.trim()) {
        newErrors[`experience.${index}.institution`] =
          "Institution name is required";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast("Error", {
        description: "Please fix the form errors before submitting",
      });
      return;
    }

    // Verify password for security changes
    if (activeTab === "security") {
      if (securityForm.newEmail && securityForm.currentPasswordForEmail) {
        const isValidEmailPassword = await verifyPassword(
          securityForm.currentPasswordForEmail
        );
        if (!isValidEmailPassword) return;
        formData.user.email = securityForm.newEmail;
      }

      if (securityForm.newPassword) {
        if (securityForm.newPassword !== securityForm.confirmPassword) {
          toast("Error", { description: "New passwords do not match" });
          return;
        }
        const isValidPassword = await verifyPassword(
          securityForm.currentPassword
        );
        if (!isValidPassword) return;
        formData.user.password = securityForm.newPassword;
      }
    }

    setIsSubmitting(true);

    try {
      // Update teacher profile
      await updateTeacherProfile(
        teacherData.user.id,
        formData,
        setIsSubmitting
      );

      // Handle qualifications
      for (const qualification of qualifications) {
        if (qualification.id && qualification.id.toString().length < 10) {
          // Check if qualification data has changed before updating
          const originalQualification = teacherData.teacher_qualifications.find(
            (q) => q.id === qualification.id
          );

          if (
            originalQualification &&
            (originalQualification.qualification !==
              qualification.qualification ||
              originalQualification.university !== qualification.university ||
              originalQualification.graduation_year !==
                qualification.graduation_year ||
              JSON.stringify(originalQualification.courses) !==
                JSON.stringify(qualification.courses || []))
          ) {
            // Only update if data has changed
            await updateTeacherQualification(
              qualification.id,
              {
                qualification: qualification.qualification,
                university: qualification.university,
                graduation_year: qualification.graduation_year,
                teacher_profile_id: teacherData.id,
                courses: qualification.courses || [],
              },
              setIsSubmitting
            );
          }
        } else {
          // New qualification - create
          await createTeacherQualification(
            {
              qualification: qualification.qualification,
              university: qualification.university,
              graduation_year: qualification.graduation_year,
              teacher_profile_id: teacherData.id,
              courses: qualification.courses || [],
            },
            setIsSubmitting
          );
        }
      }

      // Handle experiences
      for (const experience of experiences) {
        if (experience.id && experience.id.toString().length < 10) {
          // Check if experience data has changed before updating
          const originalExperience = teacherData.teacher_experiences.find(
            (e) => e.id === experience.id
          );

          if (
            originalExperience &&
            (originalExperience.type !== experience.type ||
              originalExperience.institution !== experience.institution ||
              originalExperience.year !== experience.year ||
              originalExperience.ages !== experience.ages)
          ) {
            // Only update if data has changed
            await updateTeacherExperience(
              experience.id,
              {
                type: experience.type,
                institution: experience.institution,
                year: experience.year,
                ages: experience.ages,
                teacher_profile_id: teacherData.id,
              },
              setIsSubmitting
            );
          }
        } else {
          // New experience - create
          await createTeacherExperience(
            {
              type: experience.type,
              institution: experience.institution,
              year: experience.year,
              ages: experience.ages,
              teacher_profile_id: teacherData.id,
            },
            setIsSubmitting
          );
        }
      }

      await getAllTeacherData();
      setOpen(false);
      // Reset security form
      setSecurityForm({
        newEmail: "",
        currentPasswordForEmail: "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      // Error handling is done in the API functions
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogChange = (newOpen) => {
    if (!newOpen) {
      // Reset form state when dialog is closed
      setErrors({});
      setActiveTab("personal");
      setFormData({
        user: {
          name: teacherData.user.name || null,
          user_name: teacherData.user.user_name || null,
          email: teacherData.user.email || null,
          phone: teacherData.user.phone || null,
          image: teacherData.user.image || null,
          provider: teacherData.user.provider || null,
          provider_id: teacherData.user.provider_id || null,
          lang: teacherData.user.lang || "",
        },
        address: teacherData.address || "",
        age: teacherData.age || "",
        language: teacherData.user.lang || "",
        hour_works: teacherData.hour_works || "",
        experience: teacherData.experience || "",
        rate: teacherData.rate || "",
        status: teacherData.status || "0",
        name_job: teacherData.name_job || "",
        description: teacherData.description || "",
        year_experience: teacherData.year_experience || "",
        programs,
        languages,
        subjects,
        files,
        cv: teacherData.cv || "",
        demo_video: teacherData.demo_video || "",
        teacher_experiences: experiences,
        teacher_qualifications: qualifications,
      });
      setExperiences(teacherData.teacher_experiences || []);
      setQualifications(teacherData.teacher_qualifications || []);
    }
    setOpen(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogTrigger asChild>
        <MainButton Icon={Pencil} title={"Update Profile"} />
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Update Teacher Profile</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-2 md:grid-cols-6 w-full h-24 md:h-auto">
              <TabsTrigger value="personal">Personal Info</TabsTrigger>
              <TabsTrigger value="professional">Professional</TabsTrigger>
              <TabsTrigger value="qualifications">Qualifications</TabsTrigger>
              <TabsTrigger value="experience">Experience</TabsTrigger>
              <TabsTrigger value="media">Media & Files</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
            </TabsList>

            {/* Personal Information Tab */}
            <TabsContent value="personal" className="space-y-4 ">
              <div className="flex items-center gap-4 mb-4">
                <div className="">
                  <UploadImageProfile
                    name="users"
                    disabled={false}
                    imageUrl={
                      formData.user?.image &&
                      `${UrlImage}${formData?.user?.image}`
                    }
                    onImageUploaded={(url) =>
                      setFormData({
                        ...formData,
                        user: { ...formData.user, image: url },
                      })
                    }
                    isUploading={(uploading) => setSaving(uploading)}
                  />
                </div>
                <div>
                  <h3 className="font-bold text-lg">{formData?.user?.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {formData.user.email}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label
                    htmlFor="user.name"
                    className="flex items-center gap-2"
                  >
                    <User className="size-4" /> Full Name
                  </label>
                  <Input
                    id="user.name"
                    value={formData.user.name || ""}
                    onChange={handleInputChange}
                    className={errors["user.name"] ? "border-destructive" : ""}
                  />
                  {errors["user.name"] && (
                    <p className="text-sm text-destructive flex items-center gap-2">
                      <AlertCircle className="size-4" /> {errors["user.name"]}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="user.email"
                    className="flex items-center gap-2"
                  >
                    <Mail className="size-4" /> Email
                  </label>
                  <Input
                    id="user.email"
                    type="email"
                    value={formData.user.email || ""}
                    onChange={handleInputChange}
                    className={errors["user.email"] ? "border-destructive" : ""}
                  />
                  {errors["user.email"] && (
                    <p className="text-sm text-destructive flex items-center gap-2">
                      <AlertCircle className="size-4" /> {errors["user.email"]}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="user.phone"
                    className="flex items-center gap-2"
                  >
                    <Phone className="size-4" /> Phone
                  </label>
                  <Input
                    id="user.phone"
                    value={formData.user.phone || ""}
                    onChange={handleInputChange}
                    className={errors["user.phone"] ? "border-destructive" : ""}
                  />
                  {errors["user.phone"] && (
                    <p className="text-sm text-destructive flex items-center gap-2">
                      <AlertCircle className="size-4" /> {errors["user.phone"]}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <label htmlFor="address" className="flex items-center gap-2">
                    <MapPin className="size-4" /> Address
                  </label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter your address"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="age" className="flex items-center gap-2">
                    <User className="size-4" /> Age
                  </label>
                  <Input
                    id="age"
                    type="number"
                    value={formData.age}
                    onChange={handleInputChange}
                    placeholder="Enter your age"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="language" className="flex items-center gap-2">
                    <Languages className="size-4" /> Preferred Language
                  </label>
                  <Select
                    value={formData.user.lang}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        user: { ...formData.user, lang: value },
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="ar">Arabic</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {user?.role === "admin" && (
                  <>
                    {/* Courses */}
                    <div className="space-y-2">
                      <label
                        htmlFor="language"
                        className="flex items-center gap-2"
                      >
                        <GraduationCap className="size-4" /> Courses
                      </label>
                      <AntdSelect
                        disabled={loadingCourses}
                        mode="multiple"
                        allowClear
                        className="w-full"
                        placeholder="Please select Courses"
                        defaultValue={selectCourses}
                        onChange={(value) => {
                          setSelectCourses(value);
                          setFormData({ ...formData, courses_id: value });
                        }}
                        options={courses.map((item) => ({
                          label: item.title[languages] || item.title.en,
                          value: item.id,
                        }))}
                        getPopupContainer={(triggerNode) =>
                          triggerNode.parentNode
                        }
                        dropdownStyle={{ zIndex: 9999 }}
                        virtual={false}
                      />
                    </div>
                    {/* Categories */}
                    <div className="space-y-2">
                      <label
                        htmlFor="language"
                        className="flex items-center gap-2"
                      >
                        <Box className="size-4" /> Categories
                      </label>
                      <AntdSelect
                        disabled={loadingCategories}
                        mode="multiple"
                        allowClear
                        className="w-full"
                        placeholder="Please select Categories"
                        defaultValue={selectCategories}
                        onChange={(value) => {
                          setSelectCategories(value);
                          setFormData({ ...formData, categories_id: value });
                        }}
                        options={categories.map((item) => ({
                          label: item.title[languages] || item.title.en,
                          value: item.id,
                        }))}
                        getPopupContainer={(triggerNode) =>
                          triggerNode.parentNode
                        }
                        dropdownStyle={{ zIndex: 9999 }}
                        virtual={false}
                      />
                    </div>
                  </>
                )}
              </div>
            </TabsContent>

            {/* Professional Information Tab */}
            <TabsContent value="professional" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <label htmlFor="name_job" className="flex items-center gap-2">
                    <Briefcase className="size-4" /> Job Title
                  </label>
                  <Input
                    id="name_job"
                    value={formData.name_job}
                    onChange={handleInputChange}
                    className={errors.name_job ? "border-destructive" : ""}
                  />
                  {errors.name_job && (
                    <p className="text-sm text-destructive flex items-center gap-2">
                      <AlertCircle className="size-4" /> {errors.name_job}
                    </p>
                  )}
                </div>

                <div className="space-y-2 col-span-2 sm:col-span-1">
                  <label
                    htmlFor="year_experience"
                    className="flex items-center gap-2"
                  >
                    <GraduationCap className="size-4" /> Years of Experience
                  </label>
                  <Input
                    id="year_experience"
                    type="number"
                    value={formData.year_experience}
                    onChange={handleInputChange}
                    placeholder="e.g. 5"
                  />
                </div>

                <div className="space-y-2 col-span-2 sm:col-span-1">
                  <label
                    htmlFor="hour_works"
                    className="flex items-center gap-2"
                  >
                    <Clock className="size-4" /> Hours of Work (per week)
                  </label>
                  <Input
                    id="hour_works"
                    type="number"
                    value={formData.hour_works}
                    onChange={handleInputChange}
                    placeholder="e.g. 30"
                  />
                </div>

                <div className="col-span-2 space-y-2">
                  <label htmlFor="description">Description</label>
                  <Textarea
                    id="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe your teaching style, philosophy, and approach..."
                  />
                </div>

                {/* Subjects */}
                <div className="col-span-2 space-y-2">
                  <label className="flex items-center gap-2">Subjects</label>
                  {subjects.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {subjects.map((subject, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="flex items-center gap-1 pl-3 pr-1 py-1.5"
                        >
                          <span>{subject}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 rounded-full"
                            onClick={() => removeSubject(index)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground mb-2">
                      No subjects added yet
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      placeholder="Add a subject"
                    />
                    <Button type="button" onClick={addSubject} size="sm">
                      Add
                    </Button>
                  </div>
                </div>

                {/* Languages */}
                <div className="col-span-2 space-y-2">
                  <label className="flex items-center gap-2">
                    <Languages className="size-4" /> Languages
                  </label>
                  {languages.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {languages.map((language, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="flex items-center gap-1 pl-3 pr-1 py-1.5"
                        >
                          <span>{language}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 rounded-full"
                            onClick={() => removeLanguage(index)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground mb-2">
                      No languages added yet
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      value={newLanguage}
                      onChange={(e) => setNewLanguage(e.target.value)}
                      placeholder="Add a language"
                    />
                    <Button type="button" onClick={addLanguage} size="sm">
                      Add
                    </Button>
                  </div>
                </div>

                {/* Programs */}
                <div className="col-span-2 space-y-2">
                  <label>Programs/Software</label>
                  {programs.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {programs.map((program, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="flex items-center gap-1 pl-3 pr-1 py-1.5"
                        >
                          <span>{program}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 rounded-full"
                            onClick={() => removeProgram(index)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground mb-2">
                      No programs added yet
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      value={newProgram}
                      onChange={(e) => setNewProgram(e.target.value)}
                      placeholder="Add a program"
                    />
                    <Button type="button" onClick={addProgram} size="sm">
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security" className="space-y-4">
              <Card>
                <CardContent className="space-y-4 pt-6">
                  {/* Email Change Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Change Email</h3>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <Mail className="size-4" /> New Email
                      </label>
                      <Input
                        type="email"
                        id="newEmail"
                        value={securityForm.newEmail}
                        onChange={handleSecurityInputChange}
                        placeholder="Enter new email"
                        className={errors.newEmail ? "border-destructive" : ""}
                      />
                      {errors.newEmail && (
                        <p className="text-sm text-destructive">
                          {errors.newEmail}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <Lock className="size-4" /> Current Password
                      </label>
                      <div className="relative">
                        <Input
                          type={
                            securityForm.showCurrentPasswordForEmail
                              ? "text"
                              : "password"
                          }
                          id="currentPasswordForEmail"
                          value={securityForm.currentPasswordForEmail}
                          onChange={handleSecurityInputChange}
                          placeholder="Enter current password"
                          className={
                            errors.currentPasswordForEmail
                              ? "border-destructive"
                              : ""
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                          onClick={() =>
                            setSecurityForm((prev) => ({
                              ...prev,
                              showCurrentPasswordForEmail:
                                !prev.showCurrentPasswordForEmail,
                            }))
                          }
                        >
                          {securityForm.showCurrentPasswordForEmail ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      {errors.currentPasswordForEmail && (
                        <p className="text-sm text-destructive">
                          {errors.currentPasswordForEmail}
                        </p>
                      )}
                    </div>
                    {/* <Button 
                      type="button" 
                      className="w-full sm:w-auto"
                      onClick={handleEmailUpdate}
                      disabled={loadingPasswordCheck}
                    >
                      {loadingPasswordCheck ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        "Update Email"
                      )}
                    </Button> */}
                  </div>

                  <Separator className="my-6" />

                  {/* Password Change Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Change Password</h3>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <Lock className="size-4" /> Current Password
                      </label>
                      <div className="relative">
                        <Input
                          type={
                            securityForm.showCurrentPassword
                              ? "text"
                              : "password"
                          }
                          id="currentPassword"
                          value={securityForm.currentPassword}
                          onChange={handleSecurityInputChange}
                          placeholder="Enter current password"
                          className={
                            errors.currentPassword ? "border-destructive" : ""
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                          onClick={() =>
                            setSecurityForm((prev) => ({
                              ...prev,
                              showCurrentPassword: !prev.showCurrentPassword,
                            }))
                          }
                        >
                          {securityForm.showCurrentPassword ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      {errors.currentPassword && (
                        <p className="text-sm text-destructive">
                          {errors.currentPassword}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <Lock className="size-4" /> New Password
                      </label>
                      <div className="relative">
                        <Input
                          type={
                            securityForm.showNewPassword ? "text" : "password"
                          }
                          id="newPassword"
                          value={securityForm.newPassword}
                          onChange={handleSecurityInputChange}
                          placeholder="Enter new password"
                          className={
                            errors.newPassword ? "border-destructive" : ""
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                          onClick={() =>
                            setSecurityForm((prev) => ({
                              ...prev,
                              showNewPassword: !prev.showNewPassword,
                            }))
                          }
                        >
                          {securityForm.showNewPassword ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      {errors.newPassword && (
                        <p className="text-sm text-destructive">
                          {errors.newPassword}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <Lock className="size-4" /> Confirm New Password
                      </label>
                      <div className="relative">
                        <Input
                          type={
                            securityForm.showConfirmPassword
                              ? "text"
                              : "password"
                          }
                          id="confirmPassword"
                          value={securityForm.confirmPassword}
                          onChange={handleSecurityInputChange}
                          placeholder="Confirm new password"
                          className={
                            errors.confirmPassword ? "border-destructive" : ""
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                          onClick={() =>
                            setSecurityForm((prev) => ({
                              ...prev,
                              showConfirmPassword: !prev.showConfirmPassword,
                            }))
                          }
                        >
                          {securityForm.showConfirmPassword ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      {errors.confirmPassword && (
                        <p className="text-sm text-destructive">
                          {errors.confirmPassword}
                        </p>
                      )}
                    </div>
                    {/* <Button 
                      type="button" 
                      className="w-full sm:w-auto"
                      onClick={handlePasswordUpdate}
                      disabled={loadingPasswordCheck}
                    >
                      {loadingPasswordCheck ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        "Update Password"
                      )}
                    </Button> */}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Qualifications Tab */}
            <TabsContent value="qualifications" className="space-y-4">
              {loadingQualifications ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {qualifications.map((qualification, index) => (
                    <Card key={index} className="relative border-primary/10">
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-7 w-7"
                        onClick={() => removeQualification(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2 col-span-2 sm:col-span-1">
                          <label className="flex items-center gap-2">
                            <GraduationCap className="size-4" /> Qualification
                          </label>
                          <Input
                            value={qualification.qualification}
                            onChange={(e) =>
                              updateQualification(
                                index,
                                "qualification",
                                e.target.value
                              )
                            }
                            placeholder="e.g. Master of Science in Mathematics"
                          />
                        </div>
                        <div className="space-y-2 col-span-2 sm:col-span-1">
                          <label className="flex items-center gap-2">
                            <Building className="size-4" /> University
                          </label>
                          <Input
                            value={qualification.university}
                            onChange={(e) =>
                              updateQualification(
                                index,
                                "university",
                                e.target.value
                              )
                            }
                            placeholder="e.g. Oxford University"
                          />
                        </div>
                        <div className="space-y-2 col-span-2 sm:col-span-1">
                          <label className="flex items-center gap-2">
                            <Calendar className="size-4" /> Graduation Year
                          </label>
                          <Input
                            value={qualification.graduation_year}
                            onChange={(e) =>
                              updateQualification(
                                index,
                                "graduation_year",
                                e.target.value
                              )
                            }
                            placeholder="e.g. 2020"
                          />
                        </div>
                        <div className="col-span-2 space-y-2">
                          <label>Courses</label>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {qualification.courses &&
                              qualification.courses.map(
                                (course, courseIndex) => (
                                  <Badge
                                    key={courseIndex}
                                    variant="secondary"
                                    className="flex items-center gap-1 pl-3 pr-1 py-1.5"
                                  >
                                    <span>{course}</span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-5 w-5 rounded-full"
                                      onClick={() =>
                                        removeCourse(index, courseIndex)
                                      }
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </Badge>
                                )
                              )}
                          </div>
                          <div className="flex gap-2">
                            <Input
                              value={newCourse}
                              onChange={(e) => setNewCourse(e.target.value)}
                              placeholder="Add a course"
                            />
                            <Button
                              type="button"
                              onClick={() => addCourse(index)}
                              size="sm"
                            >
                              Add
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={addQualification}
                  >
                    <PlusCircle className="h-4 w-4 mr-2" /> Add Qualification
                  </Button>
                </>
              )}
            </TabsContent>

            {/* Experience Tab */}
            <TabsContent value="experience" className="space-y-4">
              {loadingExperiences ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {experiences.map((experience, index) => (
                    <Card key={index} className="relative border-primary/10">
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-7 w-7"
                        onClick={() => removeExperience(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2">
                            <Briefcase className="size-4" /> Type
                          </label>
                          <Select
                            value={experience.type}
                            onValueChange={(value) =>
                              updateExperience(index, "type", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="online">Online</SelectItem>
                              <SelectItem value="offline">Offline</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2">
                            <Building className="size-4" /> Institution
                          </label>
                          <Input
                            value={experience.institution}
                            onChange={(e) =>
                              updateExperience(
                                index,
                                "institution",
                                e.target.value
                              )
                            }
                            placeholder="e.g. University of Michigan"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2">
                            <Calendar className="size-4" /> Year
                          </label>
                          <Input
                            value={experience.year}
                            onChange={(e) =>
                              updateExperience(index, "year", e.target.value)
                            }
                            placeholder="e.g. 2021"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2">
                            <Users2 className="size-4" /> Age Group
                          </label>
                          <Select
                            value={experience.ages}
                            onValueChange={(value) =>
                              updateExperience(index, "ages", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select age group" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="teens">Teenagers</SelectItem>
                              <SelectItem value="adults">Adults</SelectItem>
                              <SelectItem value="kids">Kids</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={addExperience}
                  >
                    <PlusCircle className="h-4 w-4 mr-2" /> Add Experience
                  </Button>
                </>
              )}
            </TabsContent>

            {/* Media & Files Tab */}
            <TabsContent value="media" className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                {/* CV Upload */}
                <div className="space-y-2">
                  <label htmlFor="cv" className="flex items-center gap-2">
                    <FileText className="size-4" /> CV/Resume
                  </label>
                  <div className="flex items-center flex-col sm:flex-row gap-2">
                    {formData.cv ? (
                      <div className="flex items-center gap-2 bg-primary/10 px-3 py-2 rounded-lg flex-1 w-full sm:w-fit">
                        <FileText className="size-5 text-primary" />
                        <span className="text-sm truncate max-w-[200px]">
                          {formData.cv}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 ml-auto"
                          onClick={() =>
                            setFormData((prev) => ({ ...prev, cv: "" }))
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 bg-muted/50 px-3 py-2 rounded-lg flex-1">
                        <AlertCircle className="size-5 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          No CV uploaded
                        </span>
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => cvInputRef.current.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" /> Upload CV
                    </Button>
                    <input
                      type="file"
                      ref={cvInputRef}
                      onChange={(e) => onFileChange(e, "cv")}
                      className="hidden"
                    />
                  </div>
                </div>

                <Separator />

                {/* Demo Video Upload */}
                <div className="space-y-2">
                  <label
                    htmlFor="demo-video"
                    className="flex items-center gap-2"
                  >
                    <Play className="size-4" /> Demo Video
                  </label>
                  <div className="flex items-center flex-col sm:flex-row gap-2">
                    {formData.demo_video ? (
                      <div className="flex items-center gap-2 bg-primary/10 px-3 py-2 rounded-lg flex-1 w-full sm:w-fit">
                        <Play className="size-5 text-primary" />
                        <span className="text-sm truncate max-w-[200px]">
                          Teaching Demo Video
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 ml-auto"
                          onClick={() =>
                            setFormData((prev) => ({ ...prev, demo_video: "" }))
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 bg-muted/50 px-3 py-2 rounded-lg flex-1">
                        <AlertCircle className="size-5 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          No demo video uploaded
                        </span>
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => demoVideoInputRef.current.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" /> Upload Video
                    </Button>
                    <input
                      type="file"
                      ref={demoVideoInputRef}
                      accept="video/*"
                      onChange={(e) => onFileChange(e, "demo_video")}
                      className="hidden"
                    />
                  </div>
                </div>

                <Separator />

                {/* Additional Files Upload */}
                <div className="space-y-2">
                  <label
                    htmlFor="additional-files"
                    className="flex items-center gap-2"
                  >
                    <FileText className="size-4" /> Additional Files
                  </label>
                  <div className="flex flex-col gap-2">
                    {formData.files && formData.files.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        {formData.files.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 bg-primary/10 px-3 py-2 rounded-lg flex-1"
                          >
                            <FileText className="size-5 text-primary" />
                            <p className="text-sm font-medium w-1/2 truncate">
                              {file}
                            </p>
                            {file.size && (
                              <p className="text-xs text-muted-foreground">
                                {file.size}
                              </p>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 ms-auto"
                              onClick={() =>
                                setFiles(files.filter((_, i) => i !== index))
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-6 bg-muted/30 rounded-lg">
                        <div className="text-center">
                          <AlertCircle className="size-10 mx-auto mb-2 text-muted-foreground/30" />
                          <p className="text-sm text-muted-foreground">
                            No files have been uploaded
                          </p>
                        </div>
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-fit"
                      onClick={() => additionalFilesInputRef.current.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" /> Upload Files
                    </Button>
                    <input
                      type="file"
                      ref={additionalFilesInputRef}
                      multiple
                      onChange={(e) => {
                        const uploadedFiles = Array.from(e.target.files || []);
                        uploadedFiles.forEach((file) =>
                          handleFileUpload(file, "additional")
                        );
                      }}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2">
            <DialogTrigger asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogTrigger>
            <Button type="submit" disabled={isSubmitting || saving}>
              {isSubmitting || saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...
                </>
              ) : (
                <>Save Changes</>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
